import Page from 'src/components/Page';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    Button,
    Container,
    useMediaQuery,
    createTheme,
    ThemeProvider,
    Skeleton,
} from '@mui/material';
import MUIDataTable, { ExpandButton } from "mui-datatables";
import _ from 'lodash';
import { debounce } from 'src/utils/debounceSearch';
import axios from 'src/utils/apiAuth';
import { showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import { CheckCircle, Cancel } from '@mui/icons-material';

const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                root: {
                    backgroundColor: '#red',
                },
                paper: {
                    boxShadow: 'none',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    // backgroundColor: 'yellow',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    "&.tss-1fz4yw6-MUIDataTableSelectCell-fixedLeft": {
                        position: "static !important",
                    },
                },
                head: {
                    // backgroundColor: 'purple',
                },
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    fontWeight: '700',
                },
                data: {
                    fontWeight: '700',
                }
            }
        },
        MUIDataTableSelectCell: {
            styleOverrides: {
                headerCell: {
                    fontWeight: '500px'
                },
            },
        },
        MuiTableFooter: {
            styleOverrides: {
                root: {
                    '& .MuiToolbar-root': {
                        //   backgroundColor: 'purple',
                    },
                },
            },
        },
    },
    typography: {
        fontFamily: "Lexend"
    }
});

const components = {
    ExpandButton: function (props) {
        return <ExpandButton {...props} />;
    }
};

const StyledButton = {
    backgroundColor: "#00AB55",
    fontFamily: "Lexend,Public Sans,sans-serif",
    fontWeight: "700",
    borderRadius: "8px",
    boxShadow: "0 8px 16px 0 rgb(0 171 85 / 10%)"
}

export default function Users() {
    const matches = useMediaQuery(theme.breakpoints.down('md'));

    const [isLoading, setIsLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(0);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState();
  
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const debouncedSearch = debounce((query) => {
        setSearchQuery(query);
    }, 1000);

    // Fetch users
    const [usersData, setUsersData] = useState([]);
    const getUsers = async (page, pageSize = 50, query = '', sortOrder = null) => {
        let apiUrl = `users-viewset?`;
        if (query) apiUrl += `search=${query}&`;
        if (page + 1 && pageSize) apiUrl += `page=${page + 1}&page_size=${pageSize}&`;
        if (sortOrder && sortOrder?.name && sortOrder?.direction) apiUrl += `sortfield=${sortOrder.name}&sortdirection=${sortOrder.direction}&`;

        // remove the trailing '&' from the URL
        if (apiUrl.endsWith("&")) apiUrl = apiUrl.slice(0, -1);

        axios.get(apiUrl)
            .then((response) => {
                if (response?.data) {
                    setUsersData(response?.data?.results?.data || []);
                    setCount(response?.data?.count || 0);
                }
            })
            .catch(error => {
                if (error) {
                    showErrorToast(error);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // MUI Data Table Options
    const options = {
        selectableRows: "none",
        selectToolbarPlacement: 'none',
        filter: false,
        search: true,
        searchOpen: false,
        print: false,
        viewColumns: true,
        responsive: "standard",
        draggableColumns: { enabled: true },
        serverSide: true,
        download: false,
        count: count,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 30, 50, 100, 200, 300, 400, 500],
        page: currentPage,
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setCurrentPage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    break;
                case 'search':
                    debouncedSearch(tableState.searchText);
                    break;
                case 'sort':
                    setSortOrder(tableState.sortOrder);
                    break;
                default:
            }
        },
    };

    // MUI Data Table Columns
    const columns = [
        {
            name: "id",
            label: "ID",
            options: {
                display: false
            },
        },
        {
            name: "name",
            label: "Name",
            options: {
                customBodyRender: (value, tableMeta, updateValue) => {
                    var id = tableMeta.rowData[0];
                    return (
                        <RouterLink
                            to={`/users/edit/${id}`}
                            state={{ fromPage: "users" }}
                            style={{
                                textDecoration: 'none',
                                color: '#00AB55',
                                fontWeight: 'bold'
                            }}>
                            {value}
                        </RouterLink>
                    )
                }
            }
        },
        {
            name: "email",
            label: "Email"
        },
        {
            name: "is_active",
            label: "Active",
            options: {
                filter: true,
                filterOptions: {
                    fullWidth: true,
                },
                customFilterListOptions: { render: v => `Is Active: ${v}` },
                customBodyRender: (value, tableMeta, updateValue) => {
                    if (value)
                        return <CheckCircle fontSize='medium' color='success' />
                    else
                        return <Cancel fontSize='medium' color='error' />
                }
            }
        }
    ];

    useEffect(() => {
        if (params.size === 0) {
            getUsers(currentPage, rowsPerPage, searchQuery, sortOrder);     
        }
    }, [currentPage, rowsPerPage, searchQuery, sortOrder, location.search]);

    return (
        <Page title="Users | CRM">
            <Container maxWidth="xl">
                <h2 align="center" style={{ fontSize: '25px', fontWeight: '800', marginBottom: '30px' }}>Users</h2>
                {isLoading ? (
                    <div style={{ padding: '16px', marginTop: "20px" }}>
                        {/* Skeleton for the Table Header */}
                        <div style={{ display: 'flex', marginBottom: '20px' }}>
                            {columns.map((column, colIndex) => (
                                <Skeleton
                                    key={colIndex}
                                    variant="text"
                                    width={'30%'}
                                    style={{ marginRight: '16px' }}
                                    height={50}
                                />
                            ))}
                        </div>
                        {/* Skeletons for the Table Rows */}
                        {Array.from(new Array(rowsPerPage)).map((_, rowIndex) => (
                            <div key={rowIndex} style={{ display: 'flex', marginBottom: '10px' }}>
                                {columns.map((column, colIndex) => (
                                    <Skeleton
                                        key={colIndex}
                                        variant="text"
                                        width={'30%'}
                                        style={{ marginRight: '16px' }}
                                        height={50}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MUIDataTable
                            title={
                                <Button
                                    variant="contained"
                                    component={RouterLink}
                                    to="/users/add"
                                    startIcon={<Icon icon={plusFill} />}
                                    style={StyledButton}
                                >
                                    Add User
                                </Button>
                            }
                            data={usersData.filter(data => data?.is_active).map(data => ({
                                id: data?.pk,
                                name: data?.first_name + " " + data?.last_name,
                                email: data?.email,
                                is_active: data?.is_active,
                            }))}
                            columns={columns}
                            options={options}
                            components={components}
                        />
                    </ThemeProvider>
                )}
            </Container>
        </Page>
    )
}